import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AddressComponent } from '../../../../shared/components/address/address.component';
import { SectionsService } from '../../../../services/sections.service';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { ListTableComponent } from '@ppl/ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs/operators';
import { LeadDetailsService } from '../../../../services/lead-details.service';
import { performValidation } from '../../../../shared/utils/validation.util';
import {
  STATIC_DATA,
  STATIC_JSON_DATA_PATH,
} from '../../../../shared/constants/static-data.constants';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { CommonModule } from '@angular/common';
import { LoggerService } from '../../../../shared/services/logger.service';
import {
  LeadDetailCommunicationUpdadeModel,
} from '../../../../interfaces/lead-detail.model';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { AuthorizeDirective } from '../../../../shared/authorization/authorize.directive.';
import { ButtonComponent } from '@ppl/ui';
import { AuthorizeFieldDirective } from '../../../../shared/authorization/authorizeField.directive';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { UserService } from '../../../../services/user.service';
import { AuthorizationUtility } from '../../../../shared/authorization/auth.utility';
import { FocusTrapDirective } from '../../../../shared/directives/focus-trap.directive';
import { DatepickerComponent } from '@ppl/ui';
import { DropdownSelectComponent } from '@ppl/ui';

@Component({
  selector: 'ppl-lead-detail-eligibility-dates',
  standalone: true,
  imports: [
    NgZorroModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AddressComponent,
    ListTableComponent,
    AuthorizeDirective,
    AuthorizeFieldDirective,
    ButtonComponent,
    NzDatePickerModule,
    FocusTrapDirective,
    DatepickerComponent,
    DropdownSelectComponent
  ],
  templateUrl: './lead-detail-eligibility-dates.component.html',
  styleUrl: './lead-detail-eligibility-dates.component.css',
})
export class LeadDetailEligibilityDatesComponent
  extends BaseComponent
  implements OnInit, OnChanges {
  /**
   * Passed to takeUntilDestroyed because the subscriptions below are set up in ngOnInit,
   * which is not an injection context — the no-argument form would throw there.
   */
  private readonly destroyRef = inject(DestroyRef);

  mode = 'read';
  filledInfo!: any;
  enrollmentStatus!: FormGroup;
  cols: any;
  gridData: any[] = [];
  communicationType: any;
  copyOfGridData: any = [];
  customErrorMessage: string = '';
  steps: any;
  entry = 'base';
  target!: LeadDetailCommunicationUpdadeModel;
  associationRole = 0;
  actionButtons: TableRowActionsModel = new TableRowActions();
  enrollmentActive: string = STATIC_DATA.ENROLLMENT_ACTIVE;
  leadStatus = '';
  leadId = '';
  program: any;
  enrollmentType = '';
  selectedViewer: any = {};
  minStartDate?: Date;
  maxEndDate?: Date;
  restoreMinStartDate?: Date;
  restoreMaxStartDate?: Date;
  disenrollMinStartDate?: Date;
  disassociatedEndDate: Date | null = null;

  get isEndDateMandatory(): boolean {
    const code = this.enrollmentStatus?.get('status')?.value;
    return code === 'Disassociated';
  }
  statusEntries: any[] = [];
  statusAddErrorMessage = 'Add Status Failed';
  statusUpdateErrorMessage = 'Update Failed';
  showStatusAddErrorMessage: boolean = false;
  allowedStatus: any[] = [];
  disableAddStatus = false;
  disableStatus = false;
  statusReasons: any[] = [];
  get isDisenrollSelected(): boolean {
    const code = this.enrollmentStatus?.get('status')?.value;
    return this.statusEntries.some(s => s.lkpValueCode === code && s.lkpValueName === 'Disenroll');
  }

  disenrollReasonList: any[] = [];
  private editAddedStatusCode: string | undefined;
  showMoveToInPPLReviewButton = false;
  userDetails: any;
  isMCO = false;
  isSectionEditable = false;
  showModifiedBy = true;

  @Input() pageId = '';
  @Input() tabId = '';
  @Input() programCode = '';
  @Input() readonly = false;
  @Input() set sectionData(data: string) {
    this.steps = data;
  }

  @Output() leadUpdated = new EventEmitter<boolean>();

  constructor(
    private fb: FormBuilder,
    private sectionService: SectionsService,
    private leadDetailsService: LeadDetailsService,
    private cdr: ChangeDetectorRef,
    private logger: LoggerService,
    private userService: UserService,
    private authUtility: AuthorizationUtility
  ) {
    super();
  }

  ngOnInit(): void {
    this.userDetails = this.userService.getUserDetails();
    this.entry = this.userService.getLeadEntry() ?? '';
    this.leadId = this.userService.getSelectedLead()!;
    this.program = this.userService.getSelectedProgram();
    this.communicationType = this.lookupService.getLookupValue('Communication_Type');
    this.statusEntries = this.entry === 'participantEnrollment' ? this.lookupService.getLookupValue('ENR_COV_Gaps') : this.lookupService.getLookupValue('prv_enr_association_status');

    if (this.userDetails?.usertype_lkp?.toLowerCase() == 'mco') {
      this.isMCO = true;
    }

    this.isSectionEditable = this.readonly ? false : this.authUtility.isSectionEditable(this.pageId, this.tabId, 'EligibilityDates');

    const userType = this.userDetails?.usertype_lkp?.toLowerCase();
    if (userType && ['provider', 'participant'].includes(userType)) {
      this.showModifiedBy = false;
    }

    const isInRole = this.program?.roles.some((role: any) => role.role_name === 'PPL Staff Tier 1') && this.userDetails.usertype_lkp === 'FMS';
    if (isInRole) {
      this.statusEntries = this.statusEntries.filter((e) => e.lkpValueCode != 'Paperwork Complete')
    }

    this.associationRole = JSON.parse(
      this.userService.getSelectedLead()!
    );

    this.getLeadStatus();
    this.createFormGroup();
    this.getFromData();
    this.getViewerData();

    this.statusReasons = this.lookupService.getLookupValue('enr_status_reason');
    this.disenrollReasonList = this.lookupService.getLookupValue('Participant-Disenroll-Reason');

    this.enrollmentStatus.get('endDate')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateMinStartDate();
    });
    this.enrollmentStatus.get('startDate')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateMaxEndDate();
    });
    this.enrollmentStatus.get('status')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((code) => {
      const selected = this.statusEntries.find(s => s.lkpValueCode === code);
      const isRestore = selected?.lkpValueName === 'Restore';
      const isDisenroll = selected?.lkpValueName === 'Disenroll';
      const isDisassociated = code === 'Disassociated';

      if (isDisassociated) {
        this.enrollmentStatus.get('endDate')?.setValidators([Validators.required]);
        this.enrollmentStatus.get('endDate')?.updateValueAndValidity();
      } else {
        this.enrollmentStatus.get('endDate')?.clearValidators();
        this.enrollmentStatus.get('endDate')?.updateValueAndValidity();
      }

      if (isDisenroll) {
        const editingStatusId = this.mode === 'edit' ? this.selectedViewer?.statusId : undefined;
        this.disenrollMinStartDate = this.getLatestPreviousStartDate(editingStatusId);
        if (this.mode !== 'edit') {
          this.enrollmentStatus.get('endDate')?.reset(null, { emitEvent: false });
        }
        this.enrollmentStatus.get('endDate')?.disable({ emitEvent: false });
      } else {
        this.disenrollMinStartDate = undefined;
        this.enrollmentStatus.get('endDate')?.enable({ emitEvent: false });
      }

      // If Restore is selected, lock the date picker between the disassociated end date and end date + 365 days
      if (isRestore && this.disassociatedEndDate) {
        this.restoreMinStartDate = new Date(this.disassociatedEndDate);
        const maxDate = new Date(this.disassociatedEndDate);
        maxDate.setDate(maxDate.getDate() + 365);
        this.restoreMaxStartDate = maxDate;
      } else {
        this.restoreMinStartDate = undefined;
        this.restoreMaxStartDate = undefined;
      }
    });
  }

  getLeadStatus() {
    // leadStatus$ is a BehaviorSubject several screens push into, so this used to re-run on
    // every lead-data refresh. distinctUntilChanged keeps it to genuine status changes, and
    // the enrollment grid is loaded once from ngOnInit rather than from here — it keys off
    // programCode / leadId, neither of which the status affects.
    this.leadDetailsService
      .getLeadStatus()
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((status: string) => {
        this.leadStatus = status;

        this.showMoveToInPPLReviewButton = false;
        this.disableAddStatus = false;
        if (this.userDetails?.usertype_lkp?.toLowerCase() == 'participant' || this.leadStatus === 'New Application' || this.leadStatus === 'In Progress' || this.leadStatus === 'Returned') {
          this.disableAddStatus = true;
        }

        if (this.userDetails?.usertype_lkp?.toLowerCase() != 'mco' && this.leadStatus === 'Returned') {
          this.showMoveToInPPLReviewButton = true;
        }

        this.buildBaseAllowedStatus();
      });
  }

  /**
   * Builds the base list of statuses offered in the Add Status dropdown from the current
   * lead status. For participant enrollment, Restore / Re-enroll are deliberately excluded
   * here; they are conditionally added back by updateAllowedStatusFromLatestRecord() only
   * when the latest enrollment record is Disassociated/Disenroll.
   */
  private buildBaseAllowedStatus(): void {
    if (this.leadStatus === 'Registration Inactive') {
      this.allowedStatus = this.statusEntries.filter(s => s.lkpValueCode === 'In PPL Review');
    } else if (this.entry === 'participantEnrollment') {
      this.allowedStatus = this.statusEntries.filter(s =>
        s.lkpValueCode != this.leadStatus &&
        s.lkpValueName !== 'Restore' &&
        s.lkpValueName !== 'Re-enroll'
      );
    } else {
      this.allowedStatus = this.statusEntries.filter(s => s.lkpValueCode != this.leadStatus);
    }
  }

  moveToPPLReview(): void {
    this.add();
    // Restrict to In PPL Review after add() has rebuilt the allowed list.
    this.allowedStatus = this.statusEntries.filter(s => s.lkpValueCode === 'In PPL Review');
    this.enrollmentStatus.patchValue({
      startDate: new Date(),
      status: 'In PPL Review'
    });
    this.disableStatus = true;
  }

  getViewerData() {
    this.leadDetailsService.getEnrollmentData(this.program?.program_code, this.leadId).subscribe((data: any) => {
      this.gridData = data?.responsedata;
      this.copyAndTransformGridData(this.gridData);
      this.updateAllowedStatusFromLatestRecord();
    });
  }

  /** Returns the most recent enrollment record (by modifiedDate), or null if none exist. */
  private getLatestEnrollmentRecord(): any {
    return this.gridData?.length
      ? this.gridData.reduce((latest: any, current: any) =>
          new Date(current.modifiedDate) > new Date(latest.modifiedDate) ? current : latest
        )
      : null;
  }

  /**
   * Adjusts the Add Status dropdown based on the latest enrollment record. For participant
   * enrollment, when the latest record is Disassociated/Disenroll the Disenroll option is hidden
   * and either Restore (within the threshold window) or Re-enroll (beyond it) is offered.
   */
  private updateAllowedStatusFromLatestRecord(): void {
    const latestRecord = this.getLatestEnrollmentRecord();

    if (this.entry === 'participantEnrollment') {
      // Disenroll's lkpValueCode is 'Disassociated'. When the participant's latest record is
      // Disenroll/Disassociated, hide the Disenroll option (already disenrolled) and anchor the
      // threshold window on its startDate. Within the window -> show Restore; beyond -> Re-enroll.
      const isLatestDisenroll = latestRecord?.status === 'Disassociated' || latestRecord?.status === 'Disenroll';
      if (isLatestDisenroll) {
        this.allowedStatus = this.allowedStatus.filter(s => s.lkpValueName !== 'Disenroll');
      }
      if (isLatestDisenroll && latestRecord?.startDate) {
        const parsedStart = new Date(latestRecord.startDate);
        const anchor = new Date(parsedStart.getUTCFullYear(), parsedStart.getUTCMonth(), parsedStart.getUTCDate(), 0, 0, 0, 0);
        this.disassociatedEndDate = anchor;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffDays = Math.floor((today.getTime() - anchor.getTime()) / (1000 * 60 * 60 * 24));
        const targetName = diffDays < STATIC_DATA.PARTICIPANT_RESTORE_REENROLL_THRESHOLD_DAYS ? 'Restore' : 'Re-enroll';
        const targetEntry = this.statusEntries.find(s => s.lkpValueName === targetName);
        if (targetEntry && !this.allowedStatus.some(s => s.lkpValueName === targetName)) {
          this.allowedStatus = [...this.allowedStatus, targetEntry];
        }
      } else {
        this.disassociatedEndDate = null;
      }
    } else {
      if (latestRecord?.status === 'Disassociated' && latestRecord?.endDate) {
        this.disassociatedEndDate = new Date(latestRecord.endDate);
      } else {
        this.allowedStatus = this.allowedStatus.filter(s => s.lkpValueName !== 'Restore');
        this.disassociatedEndDate = null;
      }
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    try {
      if (changes['sectionData'] && changes['sectionData'].currentValue) {
        this.secData = this.steps['enrollment_status'];
      }
    } catch (e) {
      this.logger.debug(
        'Error in getting section data and updating latest info',
        e
      );
    }
  }

  disabledStartDate = (startValue: Date): boolean => {
    const endDate = this.enrollmentStatus.get('endDate')?.value;
    if (!startValue || !endDate) {
      return false;
    }
    return startValue.getTime() > endDate.getTime();
  };

  disabledEndDate = (endValue: Date): boolean => {
    const startDate = this.enrollmentStatus.get('startDate')?.value;
    if (!endValue || !startDate) {
      return false;
    }
    return endValue.getTime() <= startDate.getTime();
  };

  createFormGroup() {
    this.enrollmentStatus = this.fb.group({
      statusId: [],
      participantId: [],
      startDate: [],
      endDate: [],
      status: [],
      comment: [],
      reason: []
    });
  }

  async getFromData() {
    //table column information
    try {
      if (this.entry) {
        const tempCols = await this.sectionService.getJsonConfigurationData(
          STATIC_JSON_DATA_PATH.LEAD_DETAILS_ENROLLMENT_HEADERS,
          this.entry
        );

        this.cols = tempCols?.enrollmentStatus?.columns;
        const userType = this.userDetails?.usertype_lkp?.toLowerCase();
        if (userType && ['provider', 'participant'].includes(userType)) {
          this.cols = this.cols.filter((data: any) => data.jsonpath !== 'modifiedBy');
        }
      }
    } catch (e) {
      this.logger.debug('Error in getting table headers', e);
    }
  }

  cancelEdit() {
    this.showStatusAddErrorMessage = false;
    this.mode = 'read';
    this.validationErrors = {};
    this.enrollmentStatus.reset();
    this.enrollmentType = '';
    this.selectedViewer = {};
    this.disableStatus = false;
    this.disenrollMinStartDate = undefined;
    if (this.editAddedStatusCode != null) {
      const code = this.editAddedStatusCode;
      this.allowedStatus = this.allowedStatus.filter(s => s.lkpValueCode !== code);
      this.editAddedStatusCode = undefined;
    }
    this.enrollmentStatus.get('endDate')?.enable({ emitEvent: false });
    this.enrollmentStatus.get('endDate')?.clearValidators();
    this.enrollmentStatus.get('endDate')?.updateValueAndValidity();
    setTimeout(() => this.cdr.detectChanges(), 1000);
  }


  action(event: any) {
    console.log('acto', event);
    if (event && event.action === 'edit') {
      this.mode = 'edit';
      this.editAddedStatusCode = undefined;
      if (event?.data) {
        const data = JSON.parse(JSON.stringify(event?.data));
        const tempStartDate = new Date(data.startDate);
        const tempEndDate = new Date(data.endDate);
        if (data.startDate) {
          data.startDate = new Date(tempStartDate.getUTCFullYear(), tempStartDate.getUTCMonth(), tempStartDate.getUTCDate(), 0, 0, 0, 0);
        }
        if (data.endDate) {
          data.endDate = new Date(tempEndDate.getUTCFullYear(), tempEndDate.getUTCMonth(), tempEndDate.getUTCDate(), 0, 0, 0, 0);
        }
        if (data?.status && !this.allowedStatus.some(s => s.lkpValueCode === data.status)) {
          // Statuses chosen via the dropdown exist in the ENR_COV_Gaps lookup (statusEntries), but
          // virtual workflow statuses such as In PPL Review are written by moveToPPLReview and are
          // NOT part of the lookup. Reuse the lookup entry when present; otherwise synthesize an
          // option from the stored value so the (disabled) dropdown can display the current status
          // instead of showing blank.
          const entry = this.statusEntries.find(s => s.lkpValueCode === data.status)
            ?? { lkpValueCode: data.status, lkpValueName: data.status };
          this.allowedStatus = [...this.allowedStatus, entry];
          this.editAddedStatusCode = data.status;
        }
        this.selectedViewer = data;
        this.enrollmentStatus.patchValue(data);
      }
    }
  }



  copyAndTransformGridData(gridData: any[]) {
    if (gridData) {
      const copyGridData = JSON.parse(JSON.stringify(gridData));
      copyGridData.forEach((data: any) => {
        const now = new Date().getTime();
        const start = data.startDate ? new Date(data.startDate).getTime() : null;
        const end = data.endDate ? new Date(data.endDate).getTime() : null;

        const isFuture = start && now < start;
        const isActive = start && now > start && (!end || now < end);

        if (isFuture || isActive) {
          data.actions = {
            edit: !this.isMCO && !this.readonly && this.isSectionEditable
          };
        }
      });
      this.copyOfGridData = copyGridData;
    }
  }

  updateMinStartDate(): void {
    const endDate = this.enrollmentStatus.get('endDate')?.value;
    if (endDate instanceof Date) {
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() - 1);
      this.minStartDate = nextDay;
    } else {
      this.minStartDate = undefined;
    }
  }

  updateMaxEndDate(): void {
    const startDate = this.enrollmentStatus.get('startDate')?.value;
    if (startDate instanceof Date) {
      const nextDay = new Date(startDate);
      nextDay.setDate(nextDay.getDate() + 1);
      this.maxEndDate = nextDay;
    } else {
      this.maxEndDate = undefined;
    }
  }

  private getLatestPreviousStartDate(excludeStatusId?: number): Date | undefined {
    if (!this.gridData?.length) return undefined;
    const toLocalDate = (value: string): Date | null => {
      const parsed = new Date(value);
      if (isNaN(parsed.getTime())) return null;
      return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate(), 0, 0, 0, 0);
    };
    const startTimes: number[] = [];
    for (const d of this.gridData) {
      if (excludeStatusId != null && d?.statusId === excludeStatusId) continue;
      if (d?.startDate) {
        const local = toLocalDate(d.startDate);
        if (local) startTimes.push(local.getTime());
      }
    }
    if (!startTimes.length) return undefined;
    return new Date(Math.max(...startTimes));
  }

  add() {
    this.mode = 'add';
    this.showStatusAddErrorMessage = false;
    this.enrollmentStatus.reset();
    this.enrollmentType = '';
    this.enrollmentStatus.controls['participantId'].setValue(this.associationRole);
    // Recompute the dropdown from the latest enrollment record so that, when the latest status
    // is Disassociated/Disenroll, Restore or Re-enroll is offered as required.
    this.buildBaseAllowedStatus();
    this.updateAllowedStatusFromLatestRecord();
  }

  save() {
    if (this.mode == 'add') {
      const data = { ...this.enrollmentStatus.value };

      data.startDate = this.dateFormatter.formatToString(data.startDate);

      if (data.endDate) {
        data.endDate = this.dateFormatter.formatToString(data.endDate);
      }

      try {
        this.validationErrors = performValidation(data, this.secData, 'Save', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType());
        Object.keys(this.enrollmentStatus.controls).forEach(controlName => {
          this.enrollmentStatus.get(controlName)?.markAsTouched();
        });
      } catch (e) {
        this.logger.debug('Error in validations', e);
      }

      if (!this.isDisenrollSelected && this.isEndDateMandatory && !data.endDate) {
        this.validationErrors['endDate'] = 'End Date is required.';
      }

      if (this.isDisenrollSelected) {
        const { endDate, ...rest } = this.validationErrors;
        this.validationErrors = rest;
      }

      if (this.isDisenrollSelected && !data.startDate) {
        this.validationErrors['startDate'] = 'Start Date is required.';
      }

      if (Object.keys(this.validationErrors).length === 0 && this.enrollmentStatus.valid) {
        this.leadDetailsService
          .createEnrollmentStatus(data, this.program?.program_code, this.leadId, true, this.statusAddErrorMessage)
          .subscribe((data: any) => {
            if (data.responsedata == this.statusAddErrorMessage && data.status == 200) {
              this.customErrorMessage = data.responsemsg[0].desc;
              this.showStatusAddErrorMessage = true;
              return;
            }

            this.logger.info('Status information saved successfully');
            this.cancelEdit();
            this.getViewerData();
            this.leadDetailsService.setLeadStatus(data.status);
            this.leadUpdated.emit(true);
            this.disableStatus = false;
          });
      }
    } else if (this.mode == 'edit') {
      this.updateViewer();
    }
  }

  updateViewer() {
    const data = { ...this.enrollmentStatus.value };
    data.startDate = this.dateFormatter.formatToString(data.startDate);
    if (data.endDate) {
      data.endDate = this.dateFormatter.formatToString(data.endDate);
    }
    this.leadDetailsService
      .updateEnrollmentStatus(data, this.program?.program_code, data.participantId, this.statusUpdateErrorMessage)
      .subscribe((response: any) => {
        // The API returns HTTP 200 even on a business failure (e.g. overlapping date range),
        // signalled by responsedata === 'Update Failed'. Surface the error inline and keep the
        // edit form open instead of treating it as success.
        if (response?.responsedata == this.statusUpdateErrorMessage && response?.status == 200) {
          this.customErrorMessage = response.responsemsg[0].desc;
          this.showStatusAddErrorMessage = true;
          return;
        }

        this.logger.info('Status information updated successfully');
        this.cancelEdit();
        this.getViewerData();
        this.disableStatus = false;
      });
  }
}
